import React, { useState } from 'react';
import Icon from '../../../components/appIcon';
import Image from '../../../components/appImage';
import Button from '../../../components/ui/button';
import StatusBadge from './statusBadge';

const BACKEND_URL = import.meta.env.VITE_API_URL;
const AttendanceMobileCard = ({ record, onSelect, isSelected }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatTime = (timestamp) => {
    if (!timestamp) return '--';
    return new Date(timestamp)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return '--';
    const diff = new Date(checkOut) - new Date(checkIn);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '--';
    return new Date(timestamp)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover-elevate">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(record?.id, e?.target?.checked)}
            className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
          />
          <div className="w-10 h-10 bg-muted rounded-full overflow-hidden">
            <Image
              src={record?.employee?.avatar}
              alt={record?.employee?.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">{record?.employee?.name}</h3>
            <p className="text-sm text-muted-foreground">{record?.employee?.id}</p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 hover:bg-muted rounded micro-feedback"
        >
          <Icon
            name={isExpanded ? "ChevronUp" : "ChevronDown"}
            size={16}
            className="text-muted-foreground"
          />
        </button>
      </div>
      {/* Status and Date */}
      <div className="flex items-center justify-between mb-3">
        <StatusBadge status={record?.status} size="sm" />
        <span className="text-sm text-muted-foreground">{formatDate(record?.checkIn)}</span>
      </div>
      {/* Time Information */}
      <div className="grid grid-cols-3 gap-4 mb-3">
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Check In</p>
          <p className="text-sm font-medium text-foreground">{formatTime(record?.checkIn)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Check Out</p>
          <p className="text-sm font-medium text-foreground">{formatTime(record?.checkOut)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Duration</p>
          <p className="text-sm font-medium text-foreground font-mono">
            {formatDuration(record?.checkIn, record?.checkOut)}
          </p>
        </div>
      </div>
      {/* Location */}
      <div className="flex items-center space-x-2 mb-3">
        <Icon name="MapPin" size={14} className="text-muted-foreground" />
        <span className="text-sm text-foreground">{record?.location}</span>
      </div>
      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-border pt-3 mt-3 space-y-3">
          {/* Images */}
          {record?.captureImage && (
            <div>
              <p className="text-xs text-muted-foreground mb-2">Capture Image</p>
              <div className="w-full h-24 bg-muted rounded overflow-hidden">
                <Image
                  src={`${BACKEND_URL}${record?.captureImage}`}
                  alt="Attendance capture"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          {/* Additional Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Camera ID</p>
              <p className="font-mono text-foreground">{record?.cameraId}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Confidence</p>
              <p className="font-mono text-foreground">{record?.confidenceScore || 0}%</p>
            </div>
          </div>

          {/* Notes */}
          {record?.notes && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Notes</p>
              <p className="text-sm text-foreground bg-muted p-2 rounded">{record?.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-2 pt-2">
            <Button variant="outline" size="sm" iconName="Eye">
              View
            </Button>
            <Button variant="outline" size="sm" iconName="Edit">
              Edit
            </Button>
            <Button variant="outline" size="sm" iconName="MoreHorizontal">
              More
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceMobileCard;